// Library imports
import React, { useState } from 'react';
// Component imports
import TabNav from '../../../components/TabNav';
import useScreenWidth from '../../../utils/hooks/useScreenWidth';
// Styled imports
import { InnerContent, NoDataMessage } from './styles';

const COMPACT_MAX_WIDTH = 560;
// Money% vs Bets% gap (percentage points) at or above which the split counts as a real money/public divergence.
const SHARP_GAP_PCT = 7;
// How close the bet split must sit to 50/50 (percentage points) to count as "no public lean".
const NEAR_EVEN_PCT = 6;

const TONE_STYLES = {
    sharp: { bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.35)', accent: '#fb923c', text: '#fdba74' },
    aligned: { bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.35)', accent: '#60a5fa', text: '#93c5fd' },
    neutral: { bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.25)', accent: '#94a3b8', text: '#cbd5e1' },
};

const clampPercent = value => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(100, numeric));
};

const hasMetric = value => Number.isFinite(Number(value));

const metricLabel = type => (type === 'moneyline' ? 'Moneyline' : 'Spread');

const buildRows = (game, type) => {
    const readTickets = team => (type === 'moneyline' ? team?.mlPercent : team?.spreadPercent);
    const readMoney = team => (type === 'moneyline' ? team?.mlMoneyPercent : team?.spreadMoneyPercent);

    return [game?.awayTeam, game?.homeTeam].map(team => {
        const tickets = clampPercent(readTickets(team));
        const money = clampPercent(readMoney(team));

        return {
            key: team?.teamId || team?.abbr,
            abbr: team?.abbr || team?.shortName || 'TEAM',
            color: team?.color || '#2c7ec6',
            tickets,
            money,
            delta: Number((money - tickets).toFixed(1)),
            hasData: hasMetric(readTickets(team)) || hasMetric(readMoney(team)),
        };
    });
};

// Plain-English read of the bets-vs-money split for the bottom strip.
const buildRead = (away, home, type) => {
    const betsLeader = away.tickets >= home.tickets ? away : home;
    const moneyLeader = away.money >= home.money ? away : home;
    const gap = Math.abs(away.money - away.tickets); // handle-vs-bets gap; same magnitude on both sides
    const betsEven = Math.abs(away.tickets - 50) < NEAR_EVEN_PCT;

    if (gap >= SHARP_GAP_PCT && betsLeader.key !== moneyLeader.key) {
        return {
            tone: 'sharp',
            headline: `Sharp money on ${moneyLeader.abbr}`,
            detail: `Bets lean ${betsLeader.abbr} ${Math.round(betsLeader.tickets)}%, but ${moneyLeader.abbr} draws ${Math.round(
                moneyLeader.money,
            )}% of the money (+${Math.round(gap)} pts vs bets).`,
        };
    }

    if (gap >= SHARP_GAP_PCT) {
        return {
            tone: 'aligned',
            headline: `Money loading up on ${moneyLeader.abbr}`,
            detail: `${moneyLeader.abbr} takes ${Math.round(moneyLeader.tickets)}% of bets but ${Math.round(moneyLeader.money)}% of the money (+${Math.round(
                gap,
            )} pts).`,
        };
    }

    if (!betsEven) {
        return {
            tone: 'aligned',
            headline: `Public & money agree on ${moneyLeader.abbr}`,
            detail: `${moneyLeader.abbr} leads both bets (${Math.round(moneyLeader.tickets)}%) and money (${Math.round(moneyLeader.money)}%).`,
        };
    }

    return {
        tone: 'neutral',
        headline: 'Balanced action',
        detail: `No clear public or sharp lean on the ${metricLabel(type).toLowerCase()}.`,
    };
};

// Flat team-color fill with a light top sheen and a crisp inner hairline so the block edge stays defined on the dark card.
const fillStyle = color => ({
    backgroundColor: color,
    backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 46%)',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
    transition: 'width 220ms ease',
});

const renderSplitBar = (away, home, metric, isCompact) => (
    <div
        style={{
            position: 'relative',
            flexShrink: 0,
            height: isCompact ? '30px' : '34px',
            display: 'flex',
            borderRadius: '9px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.06)',
        }}
    >
        <div style={{ width: `${away[metric]}%`, ...fillStyle(away.color) }} />
        <div
            style={{
                width: `${home[metric]}%`,
                marginLeft: 'auto',
                ...fillStyle(home.color),
                // Diagonal texture on the home side so it still reads as a separate block when both team colors are close.
                backgroundImage:
                    'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 46%), repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0, rgba(255,255,255,0.12) 5px, rgba(255,255,255,0) 5px, rgba(255,255,255,0) 10px)',
            }}
        />
        {/* Actual away/home split seam — solid, tracks the boundary */}
        <div
            style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${away[metric]}%`,
                width: '3px',
                transform: 'translateX(-1.5px)',
                background: '#ffffff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
                zIndex: 2,
            }}
        />
        {/* 50% reference marker — dotted, clearly secondary to the seam */}
        <div
            style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: 0,
                borderLeft: '2px dotted rgba(255,255,255,0.35)',
                transform: 'translateX(-1px)',
                zIndex: 1,
            }}
        />
    </div>
);

const renderMetricSection = ({ label, away, home, metric, isCompact }) => {
    const awayVal = away.hasData ? `${Math.round(away[metric])}%` : 'N/A';
    const homeVal = home.hasData ? `${Math.round(home[metric])}%` : 'N/A';
    const endLabelStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.3rem',
        fontSize: isCompact ? '0.7rem' : '0.76rem',
        fontWeight: 800,
        color: '#e2e8f0',
        whiteSpace: 'nowrap',
    };
    const dotStyle = color => ({
        width: '0.5rem',
        height: '0.5rem',
        borderRadius: '999px',
        background: color,
        flexShrink: 0,
        boxShadow: '0 0 0 1px rgba(255,255,255,0.35)',
    });

    return (
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.3rem', padding: isCompact ? '0 8px' : '0 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <span style={endLabelStyle}>
                    <span style={dotStyle(away.color)} />
                    {away.abbr} {awayVal}
                </span>
                <span
                    style={{
                        fontSize: isCompact ? '0.53rem' : '0.58rem',
                        fontWeight: 800,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: '#94a3b8',
                    }}
                >
                    {label}
                </span>
                <span style={endLabelStyle}>
                    {home.abbr} {homeVal}
                    <span style={dotStyle(home.color)} />
                </span>
            </div>
            {renderSplitBar(away, home, metric, isCompact)}
        </div>
    );
};

const renderReadStrip = (read, isCompact) => {
    const tone = TONE_STYLES[read.tone];

    return (
        <div
            style={{
                flexShrink: 0,
                display: 'flex',
                gap: '0.55rem',
                alignItems: 'stretch',
                marginTop: '10px',
                marginLeft: isCompact ? '8px' : '16px',
                marginRight: isCompact ? '8px' : '16px',
                padding: isCompact ? '0.42rem 0.55rem' : '0.48rem 0.6rem',
                borderRadius: '10px',
                background: tone.bg,
                border: `1px solid ${tone.border}`,
            }}
        >
            <span style={{ width: '3px', borderRadius: '999px', background: tone.accent, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: isCompact ? '0.78rem' : '0.84rem', fontWeight: 800, color: tone.text }}>{read.headline}</div>
                <div
                    style={{
                        fontSize: isCompact ? '0.66rem' : '0.71rem',
                        color: tone.text,
                        opacity: 0.85,
                        marginTop: '0.1rem',
                        lineHeight: 1.3,
                    }}
                >
                    {read.detail}
                </div>
            </div>
        </div>
    );
};

const PublicMoneyPercent = ({ sport, game }) => {
    const [type, setType] = useState(sport === 'mlb' || sport === 'nhl' ? 'moneyline' : 'spread');
    const screenWidth = useScreenWidth();
    const isCompact = screenWidth <= COMPACT_MAX_WIDTH;
    const [away, home] = buildRows(game, type);
    const hasAnyData = away.hasData || home.hasData;
    const read = buildRead(away, home, type);

    return (
        <InnerContent
            fluid
            style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingTop: '0.4rem', paddingBottom: '0.4rem' }}
        >
            <div style={{ flexShrink: 0 }}>
                <TabNav
                    tabs={['moneyline', 'spread']}
                    activeTab={type}
                    setTab={setType}
                    containerStyle={{ marginBottom: '0.4rem', width: '100%' }}
                    buttonStyle={{ flex: 1 }}
                    textStyle={{ width: '100%', textAlign: 'center', fontSize: '0.7rem', fontWeight: 800, padding: '0.4rem 0.6rem' }}
                />
            </div>

            {!hasAnyData ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NoDataMessage>
                        <i className="fas fa-percentage" />
                        <p>There is currently no public betting % data for this game.</p>
                    </NoDataMessage>
                </div>
            ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: isCompact ? '0.45rem' : '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexShrink: 0 }}>
                        <span style={{ fontSize: isCompact ? '0.82rem' : '0.9rem', fontWeight: 800, color: '#e2e8f0' }}>Public Betting Split</span>
                        <span
                            style={{
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: '#94a3b8',
                            }}
                        >
                            {metricLabel(type)}
                        </span>
                    </div>

                    {renderMetricSection({ label: 'Bets', away, home, metric: 'tickets', isCompact })}
                    {renderMetricSection({ label: 'Money', away, home, metric: 'money', isCompact })}

                    {renderReadStrip(read, isCompact)}
                </div>
            )}
        </InnerContent>
    );
};

export default PublicMoneyPercent;
